import {
  type ClientEnvelope,
  type OpRecord,
  type ServerEnvelope,
  ServerEnvelopeSchema,
  type SubmittedOp,
  SYNC_PROTOCOL_VERSION,
  type VisibilityAddedEnvelope,
  type VisibilityRemovedEnvelope,
} from '@dl/protocol';
import { type Hlc, type OpId } from '@dl/shared';

/**
 * The Replica surface SyncClient depends on. Declared structurally so a
 * `Comlink.Remote<ReplicaWorker>` (which forwards these same calls to a
 * worker-resident `Replica` instance) satisfies the contract without a
 * cast — the in-tree `Replica` class can't be passed directly because its
 * `private` fields make it nominal.
 *
 * **Every member is required, and must stay required.** These were once
 * optional, guarded by `if (!this.replica.someMethod) return;` capability
 * probes, so that a minimal test double needn't implement the whole surface.
 * That is unsound against the ONE implementation that matters in production:
 * a Comlink proxy returns a truthy, callable proxy for *every* property
 * access, so the probe always passes, the call crosses into the worker, and a
 * method missing from the `ReplicaWorker` facade resolves to
 * `undefined.apply(...)` — surfacing as `TypeError: Cannot read properties of
 * undefined (reading 'apply')` and the "Storage unavailable — saves will not
 * work" banner. Optionality bought test-double convenience at the cost of
 * making the real drift invisible to the type system; it shipped that bug
 * twice (the #62/#80 watermarks, then the durable outbox).
 *
 * Required members make `worker-entry.ts`'s `_facadeSatisfiesReplicaLike`
 * assertion a real gate. Test doubles use the `fakeReplica()` factory in
 * `sync-client.test.ts`, which fills in no-op defaults — the convenience is
 * preserved without weakening the contract.
 */
export interface ReplicaLike {
  appendLocal(op: SubmittedOp<unknown>): Promise<OpRecord<unknown>>;
  applyServerOp(record: OpRecord<unknown>): Promise<void>;
  /**
   * Remove a locally-authored op the server rejected (stale/invariant) so it
   * stops being folded as canon. See Replica.removeLocalOp.
   */
  removeLocalOp(id: OpId): Promise<void>;
  /**
   * Batched server-op apply (#sync perf) — the SyncClient flushes buffered
   * op_push frames through it in one call (one Comlink round-trip + one
   * transaction) rather than per-op `applyServerOp`.
   */
  applyServerOps(records: readonly OpRecord<unknown>[]): Promise<void>;
  /** III.A.2 (#62) — per-Viewer watermark store. */
  getViewerWatermark(viewerKeyStr: string): Promise<{ wall_ms: number; logical: number } | null>;
  setViewerWatermark(
    viewerKeyStr: string,
    hlc: { wall_ms: number; logical: number },
  ): Promise<void>;
  /**
   * III.A.2 (#80) — the highest HLC durably present in the local op log, or
   * null when empty. `connect()` clamps the resume watermark to it so a
   * persisted watermark can never skip ops the local log is missing.
   */
  maxOpHlc(): Promise<{ wall_ms: number; logical: number } | null>;
  /**
   * Durable outbox (audit fix) — every locally-authored op still awaiting
   * server arbitration (`confirmed_hlc IS NULL`). `connect()` re-enqueues these
   * so a submit→reload→reconnect re-flushes them instead of silently dropping a
   * write that is still folded as canon.
   */
  listUnconfirmedOps(): Promise<OpRecord<unknown>[]>;
}

/** True when HLC `a` is strictly less than HLC `b`. */
function hlcLess(
  a: { wall_ms: number; logical: number },
  b: { wall_ms: number; logical: number },
): boolean {
  return a.wall_ms < b.wall_ms || (a.wall_ms === b.wall_ms && a.logical < b.logical);
}

/**
 * A draft-with-conflict surface — an op the client wrote locally but the
 * server rejected as stale (per 02-architecture.md §4.1). The UI in
 * E4 (#21) renders these alongside the queue.
 */
export interface DraftWithConflict {
  client_op_id: OpId;
  local: OpRecord<unknown>;
  current_head: OpId;
  current_op: OpRecord<unknown>;
}

/**
 * An op the server refused because it violates a substrate invariant (bad
 * shape, unauthorized write, id conflict, …) — distinct from a stale causal
 * conflict. The local provisional op is removed from the replica; the UI should
 * surface the error to the author rather than silently swallowing it.
 */
export interface InvariantRejection {
  client_op_id: OpId;
  code: string;
  message: string;
}

export interface SyncClientOptions {
  campaignId: string;
  url: string;
  /**
   * The viewer key (`kind:id`) this client connects as (III.A.2 / #62).
   * Used to look up the persisted per-Viewer watermark in the replica
   * for resume-on-reconnect. Defaults to `'gm:self'` for back-compat
   * with the pre-#62 single-viewer call sites.
   */
  viewerKey?: string;
  /** Builds the WebSocket — overrideable for tests. */
  createSocket?: (url: string) => WebSocket;
  /** Called when an op is rejected as stale. */
  onConflict?: (conflict: DraftWithConflict) => void;
  /**
   * Called when an op is rejected for violating a substrate invariant (not a
   * stale causal conflict). Without a handler for this frame the submit promise
   * would hang forever and the op would be re-sent on every reconnect.
   */
  onInvariantRejected?: (rejection: InvariantRejection) => void;
  /**
   * Called after a rejected local op has been removed from the replica, so the
   * view layer re-folds the op log and the demoted op disappears from the UI.
   */
  onLocalOpRemoved?: (id: OpId) => void;
  /**
   * Automatic reconnect after an unexpected socket close. Default ON. Set
   * `enabled: false` to opt out (some tests). `baseMs`/`maxMs` bound the
   * exponential backoff; the actual delay is jittered to avoid a reconnect
   * storm when many clients drop together (e.g. a worker deploy). A scheduler
   * override is provided for deterministic tests.
   */
  reconnect?: {
    enabled?: boolean;
    baseMs?: number;
    maxMs?: number;
    /** Test seam: schedule a delayed callback (defaults to setTimeout). */
    schedule?: (fn: () => void, delayMs: number) => void;
    /** Test seam: jitter multiplier in [0,1) (defaults to Math.random). */
    random?: () => number;
  };
  /** Called when the connection state changes. */
  onConnectionChange?: (connected: boolean) => void;
  /**
   * Called whenever the replica has just received a server-canonical op
   * (server push, backfill, or local op_accept). The view layer uses this
   * to re-fold the op log into its entity cache without polling.
   */
  onOpApplied?: (op: OpRecord<unknown>) => void;
  /**
   * Called when the server pushes a `visibility_added` envelope (III.A.3
   * / #63). Non-GM clients use this to populate their projected fact
   * view; GM clients should never observe this kind (the server emits
   * raw `op_push` to GM viewers via the identity-projection path).
   */
  onVisibilityAdded?: (envelope: VisibilityAddedEnvelope) => void;
  /**
   * Called when the server pushes a `visibility_removed` envelope
   * (III.A.3 / #63). The envelope carries ONLY (subject, predicate) —
   * never the value of the removed fact. The client SHOULD drop the
   * (subject, predicate) row from its projected view.
   */
  onVisibilityRemoved?: (envelope: VisibilityRemovedEnvelope) => void;
}

type SubmitResult =
  | { kind: 'accepted'; op: OpRecord<unknown> }
  | ({ kind: 'conflict' } & DraftWithConflict)
  | ({ kind: 'rejected' } & InvariantRejection);
type PendingResolver = (result: SubmitResult) => void;

/**
 * Client-side WebSocket sync. Wraps a Replica with a connection to the
 * server's /v1/sync endpoint. Locally-authored ops are written to the
 * replica immediately (appendLocal) and queued for server arbitration;
 * server-accepted ops are applied to the replica via applyServerOp.
 *
 * On stale rejection, the conflict is surfaced via `onConflict` so the UI
 * (E4) can render the draft-with-conflict entry.
 *
 * Reconnection: when the socket closes, queued ops persist in memory and
 * re-flush on next connect. The server's hello/backfill loop handles
 * catch-up of ops missed during the disconnect window.
 */
export class SyncClient {
  private socket: WebSocket | undefined;
  private connected = false;
  private lastSeenHlc: Hlc | null = null;
  private readonly pending = new Map<OpId, PendingResolver>();
  private readonly queue: {
    client_op_id: OpId;
    op: SubmittedOp<unknown>;
    // The actual locally-appended record (real id + local HLC), so a stale
    // conflict can surface the USER's op, not the server's winning op.
    local: OpRecord<unknown>;
  }[] = [];
  // #sync perf — incoming op_push frames buffer here and apply in
  // batches (one Comlink round-trip + one transaction) instead of one
  // call per op. Flushed when the buffer hits OP_PUSH_BATCH or after a
  // short idle so live single ops stay low-latency.
  private readonly opPushBuffer: OpRecord<unknown>[] = [];
  private opPushFlushTimer: ReturnType<typeof setTimeout> | null = null;
  // Reconnect state. `intentionalClose` suppresses reconnect after close();
  // `reconnectAttempts` drives the exponential backoff; `reconnecting` guards
  // against overlapping reconnect schedules.
  private intentionalClose = false;
  private reconnectAttempts = 0;
  private reconnecting = false;
  private opPushFlushing = false;
  private static readonly OP_PUSH_BATCH = 128;
  private static readonly OP_PUSH_FLUSH_MS = 12;

  constructor(
    private readonly replica: ReplicaLike,
    private readonly opts: SyncClientOptions,
  ) {}

  /** Open the WebSocket and send hello. Resolves on ack. */
  async connect(): Promise<void> {
    // An explicit connect() call is intentional — clear any prior close-suppress
    // so a later unexpected drop reconnects again.
    this.intentionalClose = false;
    // Load the persisted per-Viewer watermark (#62). This is the source of
    // truth for resume — the in-memory `lastSeenHlc` is only the per-session
    // counter.
    const viewerKeyStr = this.opts.viewerKey ?? 'gm:self';
    const stored = await this.replica.getViewerWatermark(viewerKeyStr);
    if (stored) this.lastSeenHlc = stored;
    // #80 — clamp the resume watermark to what's durably in the local op
    // log. The watermark advances per applied op; if an op's row write
    // didn't flush before a reload (or was otherwise dropped) the
    // watermark can sit AHEAD of the local log, so a `since-watermark`
    // backfill would skip ops the local log never received (tail gap).
    // Resuming from min(persisted, localMax) re-delivers anything past
    // the local max; re-applying ops already present is idempotent. An
    // empty local log resumes from genesis (full backfill).
    if (this.lastSeenHlc) {
      const localMax = await this.replica.maxOpHlc();
      if (!localMax) {
        this.lastSeenHlc = null;
      } else if (hlcLess(localMax, this.lastSeenHlc)) {
        this.lastSeenHlc = localMax;
      }
    }

    // Durable outbox (audit fix) — re-enqueue every locally-authored op the
    // replica still holds unconfirmed. Without this, a reload between submit and
    // the accept ACK loses the in-memory queue while the op stays folded as
    // canon — the write silently never reaches the server. Re-flushed ops carry
    // the SAME id; the DO idempotently ACKs one already in its log, so a re-flush
    // of an op the previous session actually landed is a no-op, not a duplicate.
    await this.reenqueueUnconfirmed();

    const factory = this.opts.createSocket ?? ((url) => new WebSocket(url));
    const socket = factory(this.opts.url);
    this.socket = socket;
    await new Promise<void>((resolve, reject) => {
      const onOpen = (): void => {
        socket.removeEventListener('error', onError);
        resolve();
      };
      const onError = (event: Event): void => {
        socket.removeEventListener('open', onOpen);
        reject(new Error(`WebSocket failed to open: ${String(event.type)}`));
      };
      socket.addEventListener('open', onOpen, { once: true });
      socket.addEventListener('error', onError, { once: true });
    });
    socket.addEventListener('message', (event) => this.handleMessage(event));
    socket.addEventListener('close', () => this.handleClose());

    this.send({
      kind: 'hello',
      protocol_version: SYNC_PROTOCOL_VERSION,
      last_seen_hlc: this.lastSeenHlc,
      campaign_id: this.opts.campaignId,
    });
  }

  private async persistWatermark(hlc: Hlc): Promise<void> {
    const viewerKeyStr = this.opts.viewerKey ?? 'gm:self';
    await this.replica.setViewerWatermark(viewerKeyStr, hlc);
  }

  /**
   * Durable outbox (audit fix) — load every unconfirmed op from the replica and
   * push any not already tracked (in the live queue or an in-flight pending
   * submit) onto the queue, so `flushAll` re-sends them once the handshake acks.
   * Reconstructs the `SubmittedOp` shape from the persisted record; the record
   * itself is the `local` surface a later stale conflict would display.
   */
  private async reenqueueUnconfirmed(): Promise<void> {
    const unconfirmed = await this.replica.listUnconfirmedOps();
    for (const rec of unconfirmed) {
      if (this.pending.has(rec.id)) continue;
      if (this.queue.some((q) => q.client_op_id === rec.id)) continue;
      const op: SubmittedOp<unknown> = {
        subject: rec.subject,
        predicate: rec.predicate,
        payload: rec.payload,
        base_version: rec.base_version,
        author: rec.author,
        created_at_real: rec.created_at_real,
      };
      this.queue.push({ client_op_id: rec.id, op, local: rec });
    }
  }

  /** Remove a settled op from the submit queue (idempotent). */
  private dequeue(id: OpId): void {
    const idx = this.queue.findIndex((q) => q.client_op_id === id);
    if (idx >= 0) this.queue.splice(idx, 1);
  }

  /**
   * Submit a locally-authored op. The op is written to the local replica
   * immediately and queued for server arbitration. Resolves with the
   * server's response (accepted op or conflict).
   *
   * `onLocalAppend` fires as soon as the op is in the LOCAL op log, i.e. as
   * soon as it is visible to `getHead`. That is the barrier a caller needs to
   * sequence a follow-up read-modify-write on the same chain: the returned
   * promise resolves only on the server ack, which is far too late — a second
   * write issued in the meantime would read a stale head, carry a duplicate
   * `base_version`, and be arbitrated away as stale. (See
   * `apps/client/src/sequence-by-key.ts` for the config-write case that made
   * this necessary.) Optional, so every existing call site is unaffected.
   */
  async submit(
    op: SubmittedOp<unknown>,
    onLocalAppend?: (record: OpRecord<unknown>) => void,
  ): Promise<SubmitResult> {
    const localRecord = await this.replica.appendLocal(op);
    const clientOpId = localRecord.id;
    const entry = { client_op_id: clientOpId, op, local: localRecord };
    this.queue.push(entry);
    onLocalAppend?.(localRecord);

    // Optimistic local fold: surface the just-appended op to the view
    // layer immediately, so subsequent in-app reads (entity list,
    // navigate-back, etc.) see the GM's own write before the server
    // ACK arrives. Without this, an in-app navigation between submit
    // and op_accept shows a stale view (the user's reported bug — they
    // see "Saved ✓" but the entity is absent from the list).
    //
    // Op log invariant preserved: appendLocal is the source of truth for
    // the local provisional record; the server's eventual op_accept (or
    // op_reject_stale) replaces or invalidates it. The view re-fold runs
    // on EVERY onOpApplied so the optimistic state is overwritten by the
    // server-canonical version on accept.
    this.opts.onOpApplied?.(localRecord);

    return new Promise((resolve) => {
      this.pending.set(clientOpId, (result) => {
        // De-queue so a resolved op is never re-sent on reconnect. (The message
        // handlers also dequeue before resolving; this is idempotent.)
        this.dequeue(clientOpId);
        resolve(result);
      });
      if (this.connected) this.flushOne(entry);
    });
  }

  /** Cleanly close the WebSocket. Suppresses automatic reconnect. */
  close(): void {
    this.intentionalClose = true;
    if (this.opPushFlushTimer) {
      clearTimeout(this.opPushFlushTimer);
      this.opPushFlushTimer = null;
    }
    this.socket?.close();
    this.socket = undefined;
    this.connected = false;
  }

  private send(envelope: ClientEnvelope): void {
    this.socket?.send(JSON.stringify(envelope));
  }

  private flushOne(entry: { client_op_id: OpId; op: SubmittedOp<unknown> }): void {
    this.send({ kind: 'op', client_op_id: entry.client_op_id, op: entry.op });
  }

  private flushAll(): void {
    for (const entry of [...this.queue]) {
      this.flushOne(entry);
    }
  }

  /**
   * Remove a server-rejected provisional op from the local replica so it stops
   * folding as canon, then signal the view to re-fold.
   */
  private async demoteRejected(id: OpId): Promise<void> {
    try {
      await this.replica.removeLocalOp(id);
      this.opts.onLocalOpRemoved?.(id);
    } catch {
      // Best-effort: a failed demotion leaves the op in place (prior
      // behaviour); the conflict/rejection is still surfaced to the author.
    }
  }

  /** Schedule a batched apply of buffered op_push frames (#sync perf). */
  private scheduleOpPushFlush(): void {
    if (this.opPushBuffer.length >= SyncClient.OP_PUSH_BATCH) {
      void this.flushOpPushBuffer();
      return;
    }
    if (this.opPushFlushTimer) return;
    this.opPushFlushTimer = setTimeout(() => {
      this.opPushFlushTimer = null;
      void this.flushOpPushBuffer();
    }, SyncClient.OP_PUSH_FLUSH_MS);
  }

  /**
   * Drain the op_push buffer through the batched apply path, in arrival
   * (HLC) order. Serialised via `opPushFlushing` so overlapping triggers
   * (size threshold + timer) don't double-apply; ops buffered during a
   * flush are picked up by the while-loop. `onOpApplied` fires per op
   * (drives the coalesced view rebuild + sync indicator); the watermark
   * persists once per batch.
   */
  private async flushOpPushBuffer(): Promise<void> {
    if (this.opPushFlushTimer) {
      clearTimeout(this.opPushFlushTimer);
      this.opPushFlushTimer = null;
    }
    if (this.opPushFlushing || this.opPushBuffer.length === 0) return;
    this.opPushFlushing = true;
    try {
      while (this.opPushBuffer.length > 0) {
        const batch = this.opPushBuffer.splice(0, this.opPushBuffer.length);
        await this.replica.applyServerOps(batch);
        for (const record of batch) this.opts.onOpApplied?.(record);
        await this.persistWatermark(batch[batch.length - 1]!.hlc);
      }
    } finally {
      this.opPushFlushing = false;
    }
  }

  private handleMessage(event: MessageEvent): void {
    const text = typeof event.data === 'string' ? event.data : new TextDecoder().decode(event.data as ArrayBuffer);
    let parsed: ServerEnvelope;
    try {
      parsed = ServerEnvelopeSchema.parse(JSON.parse(text));
    } catch {
      return;
    }
    switch (parsed.kind) {
      case 'ack':
        this.connected = true;
        // A completed handshake means the connection is healthy — reset the
        // backoff so the NEXT unexpected drop retries quickly.
        this.reconnectAttempts = 0;
        this.opts.onConnectionChange?.(true);
        this.flushAll();
        return;
      case 'op_push': {
        // Buffer and batch-apply (#sync perf). Watermark advances to the
        // arrival tip immediately; the actual apply + persist happen on
        // the next flush.
        this.opPushBuffer.push(parsed.op);
        this.lastSeenHlc = parsed.op.hlc;
        this.scheduleOpPushFlush();
        return;
      }
      case 'op_accept': {
        // 2026-05-26 history-duplicate fix: the server uses `client_op_id` as
        // the canonical op_log id (see `sync-handler.ts:handleOp`), so the
        // accepted record's id matches the provisional row's id. `applyServerOp`
        // detects the existing row and stamps the canonical HLC + marks it
        // confirmed (durable outbox) — no second row, no duplicate versions.
        //
        // This runs whether or not a pending resolver exists: a re-enqueued op
        // after a reload (durable outbox) has no live resolver, but MUST still be
        // applied+confirmed+dequeued so it stops being re-flushed every connect.
        const accepted = parsed.op;
        const resolver = this.pending.get(parsed.client_op_id);
        if (resolver) this.pending.delete(parsed.client_op_id);
        this.dequeue(parsed.client_op_id);
        void this.replica
          .applyServerOp(accepted)
          .then(() => this.opts.onOpApplied?.(accepted))
          .then(() => this.persistWatermark(accepted.hlc));
        this.lastSeenHlc = accepted.hlc;
        resolver?.({ kind: 'accepted', op: accepted });
        return;
      }
      case 'op_reject_stale': {
        // Runs with or without a live resolver — a re-enqueued op (durable
        // outbox) rejected on reconnect must still be demoted + dequeued +
        // surfaced as a conflict, else it re-flushes forever.
        const resolver = this.pending.get(parsed.client_op_id);
        if (resolver) this.pending.delete(parsed.client_op_id);
        // Surface the USER's local op (from the queue), NOT the server's
        // winning op. Reconstruct from the wire only if the queue entry is gone.
        const queued = this.queue.find((q) => q.client_op_id === parsed.client_op_id);
        const local: OpRecord<unknown> = queued?.local ?? {
          id: parsed.client_op_id,
          subject: parsed.current_op.subject,
          predicate: parsed.current_op.predicate,
          payload: parsed.current_op.payload,
          base_version: null,
          hlc: parsed.current_op.hlc,
          author: parsed.current_op.author,
          created_at_real: parsed.current_op.created_at_real,
        };
        this.dequeue(parsed.client_op_id);
        // Demote the rejected provisional op OUT of the local fold — otherwise
        // its (client-merged) HLC sits above the server tip and folds as the
        // current value forever, silently letting the stale write "win"
        // locally. The server's winning op arrives via backfill/op_push.
        void this.demoteRejected(parsed.client_op_id);
        const conflict: DraftWithConflict = {
          client_op_id: parsed.client_op_id,
          local,
          current_head: parsed.current_head,
          current_op: parsed.current_op,
        };
        this.opts.onConflict?.(conflict);
        resolver?.({ kind: 'conflict', ...conflict });
        return;
      }
      case 'op_reject_invariant': {
        // Previously unhandled: the pending resolver never fired (submit hung
        // forever) and the op was re-sent on every reconnect. Now: dequeue,
        // remove the invalid provisional op from the fold, and surface the error.
        const resolver = this.pending.get(parsed.client_op_id);
        const rejection: InvariantRejection = {
          client_op_id: parsed.client_op_id,
          code: parsed.code,
          message: parsed.message,
        };
        this.dequeue(parsed.client_op_id);
        void this.demoteRejected(parsed.client_op_id);
        this.opts.onInvariantRejected?.(rejection);
        if (resolver) {
          this.pending.delete(parsed.client_op_id);
          resolver({ kind: 'rejected', ...rejection });
        }
        return;
      }
      case 'watermark':
        this.lastSeenHlc = parsed.server_hlc;
        void this.persistWatermark(parsed.server_hlc);
        return;
      case 'visibility_added':
        this.opts.onVisibilityAdded?.(parsed);
        this.lastSeenHlc = parsed.hlc;
        void this.persistWatermark(parsed.hlc);
        return;
      case 'visibility_removed':
        this.opts.onVisibilityRemoved?.(parsed);
        this.lastSeenHlc = parsed.hlc;
        void this.persistWatermark(parsed.hlc);
        return;
      case 'ping':
        this.send({ kind: 'pong' });
        return;
      case 'pong':
        return;
    }
  }

  private handleClose(): void {
    this.connected = false;
    this.opts.onConnectionChange?.(false);
    this.socket = undefined;
    // Queued ops persist in memory and re-flush on the next successful connect;
    // pending resolvers resolve when the same client_op_id round-trips. Without
    // automatic reconnect a single dropped socket (sleep/wake, network blip,
    // worker deploy, DO hibernation eviction) left the client offline until a
    // full page reload — silently accumulating unsynced writes.
    if (!this.intentionalClose) this.scheduleReconnect();
  }

  /**
   * Schedule a reconnect with jittered exponential backoff. Jitter spreads the
   * herd when many clients drop together (e.g. a worker deploy). No-op if
   * reconnect is disabled, a reconnect is already pending, or the close was
   * intentional.
   */
  private scheduleReconnect(): void {
    if (this.opts.reconnect?.enabled === false) return;
    if (this.reconnecting || this.intentionalClose) return;
    this.reconnecting = true;
    const baseMs = this.opts.reconnect?.baseMs ?? 500;
    const maxMs = this.opts.reconnect?.maxMs ?? 30_000;
    const rand = this.opts.reconnect?.random ?? Math.random;
    const schedule =
      this.opts.reconnect?.schedule ?? ((fn, ms) => void setTimeout(fn, ms));
    const backoff = Math.min(maxMs, baseMs * 2 ** this.reconnectAttempts);
    // Full jitter over [0.5, 1.0) × backoff so retries never collapse to 0.
    const delay = backoff * (0.5 + rand() * 0.5);
    this.reconnectAttempts += 1;
    schedule(() => {
      this.reconnecting = false;
      void this.reconnect();
    }, delay);
  }

  private async reconnect(): Promise<void> {
    if (this.intentionalClose) return;
    try {
      await this.connect();
      // connect() resolves on socket-open; the backoff resets on the hello ACK.
    } catch {
      // Still unreachable — back off further and retry.
      this.scheduleReconnect();
    }
  }
}
